/**
 * The purchase API (agent wallet P3): the person's side of a purchase. Every refusal comes back as the server's code
 * (busy, still_on_payment_page, not_waiting), read from the body's `error` field (ApiError.code), never from text.
 * The person's budgets come from P1's getAgentBudgets (./agent-budgets), not from here.
 */
import type { AgentPurchaseView } from "@bash/shared";
import { ApiError, authedFetch } from "./client";

export type PurchaseActionResult =
  | { ok: true; purchase: AgentPurchaseView }
  | { ok: false; code: string };

const path = (id: string, verb?: string) =>
  `/api/purchases/${encodeURIComponent(id)}${verb ? `/${verb}` : ""}`;

/** GET /api/purchases/active: the person's pay step as the server has it. */
export interface ActivePayStep {
  /** The open pay step (approved or paying), or null. */
  purchase: AgentPurchaseView | null;
  /** How long ago (ms, on the server's clock) the person's last pay step ended; null if they never had one. */
  lastEndedMsAgo: number | null;
}

/** The person's open pay step and when their last one ended: the server's word, not a card's. Throws on any refusal. */
export async function getActivePayStep(): Promise<ActivePayStep> {
  const res = await authedFetch("/api/purchases/active");
  return (await res.json()) as ActivePayStep;
}

/** The server's purchase: its amount, its shop, its status. Throws on any refusal. */
export async function getPurchase(id: string): Promise<AgentPurchaseView> {
  const res = await authedFetch(path(id));
  return (await res.json()) as AgentPurchaseView;
}

async function act(url: string, body: unknown = {}): Promise<PurchaseActionResult> {
  try {
    const res = await authedFetch(url, {
      method: "POST",
      body: JSON.stringify(body),
    });
    const json = (await res.json()) as { purchase: AgentPurchaseView };
    return { ok: true, purchase: json.purchase };
  } catch (err) {
    if (err instanceof ApiError)
      return { ok: false, code: err.code ?? `http_${err.status}` };
    return { ok: false, code: "unavailable" };
  }
}

export const approvePurchase = (id: string) => act(path(id, "approve"));
export const declinePurchase = (id: string) => act(path(id, "decline"));
export const finishPayStep = (id: string, force = false) =>
  act(path(id, "done"), { force });
export const resolveUnknownPurchase = (
  id: string,
  outcome: "paid" | "failed"
) => act(path(id, "resolve"), { outcome });
